import { Form, useForm } from '@magicdoorinc/form';
import { Button, IconLoader, useLocalization, Modal, IconLock } from '@magicdoorinc/ui';
import { createMemo, createSignal, onCleanup, createEffect, Show } from 'solid-js';
import IconArrow from '~/assets/images/common/lineArrow.svg?component-solid';
import LabeledBankingInput from '~/components/common/Inputs/form-inputs/LabeledBankingInput';
import { useCompanySettings, usePortalPaymentAccounts } from '~/contexts/global';
import { IconCards } from '~/pages/tenants/tenant-details/components/TenantPaymentDetailsModal';
import type { TrialPaymentMethodModalProps } from './trialPaymentMethodModal.types';

const SectionHeader = (props: { title: string; subtitle: string; onBack: () => void }) => (
  <div class="flex items-center gap-4">
    <Button variant="outlined" onClick={props.onBack} class="px-2 pb-1" size="sm">
      <IconArrow class="size-4 rotate-90" />
    </Button>
    <div>
      <h3 class="text-lg font-semibold text-gray-900 sm:text-xl">{props.title}</h3>
      <p class="mt-1 text-sm text-gray-600">{props.subtitle}</p>
    </div>
  </div>
);

const PaymentMethodButton = (props: { onClick: () => void; disabled: boolean; title: string; subtitle: string }) => (
  <Button
    onClick={props.onClick}
    class="group flex h-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-gray-200 p-6 transition-all duration-200"
    variant="outlined"
    disabled={props.disabled}>
    <div class="flex size-12 items-center justify-center rounded-lg">
      <IconCards />
    </div>
    <div class="text-center">
      <div class="text-base font-semibold text-text-01 sm:text-base">{props.title}</div>
      <div class="text-xs text-text-02 sm:text-sm">{props.subtitle}</div>
    </div>
  </Button>
);

export const PortalTrialPaymentMethodGate = (props: TrialPaymentMethodModalProps) => {
  const { t } = useLocalization();
  const {
    getStripe,
    getStripeCheckoutInfo,
    paymentAccountsActions,
    pollCheckoutResult,
    validatedPaymentAccountsOptions,
    createPaymentAccount,
    validatedPaymentAccounts,
  } = usePortalPaymentAccounts();
  const { companySettingsActions } = useCompanySettings();

  const [paymentMethod, setPaymentMethod] = createSignal<'stripe' | 'plaid' | null>(null);
  const [isProcessing, setIsProcessing] = createSignal<boolean>(false);

  const plaidForm = useForm();

  let checkoutInstance: any = null;

  const isStripeReady = createMemo(() => !getStripe.loading && !getStripeCheckoutInfo.loading);

  const cleanupStripe = () => {
    if (checkoutInstance) {
      checkoutInstance.unmount();
      checkoutInstance.destroy();
      checkoutInstance = null;
    }
    setIsProcessing(false);
  };

  const handleClose = () => {
    setPaymentMethod(null);
    cleanupStripe();
    props.onClose();
  };

  const initStripeCheckout = async () => {
    setIsProcessing(true);

    const timeout = setTimeout(() => {
      console.error('Stripe initialization timeout');
      setIsProcessing(false);
    }, 10000);

    try {
      const stripe = await getStripe();
      const checkout = await getStripeCheckoutInfo();

      if (stripe && checkout) {
        checkoutInstance = await stripe.initEmbeddedCheckout({
          clientSecret: checkout.clientSecret,
          onComplete: async () => {
            try {
              await pollCheckoutResult(checkout.sessionId);

              await paymentAccountsActions.refetch();
              if (validatedPaymentAccountsOptions().length === 0) {
                await companySettingsActions.refetch();
              }

              setTimeout(() => {
                const currentPaymentMethods = validatedPaymentAccounts();
                if (currentPaymentMethods && currentPaymentMethods.length > 0) {
                  props.onSuccess?.();
                  handleClose();
                  window.location.reload();
                } else {
                  console.error('Payment method not detected after successful payment');
                  setIsProcessing(false);
                }
              }, 1000);
            } catch (error) {
              console.error('Payment setup failed:', error);
              setIsProcessing(false);
            } finally {
              clearTimeout(timeout);
            }
          },
        });

        checkoutInstance.mount('#stripe-checkout-form');
        clearTimeout(timeout);
      } else {
        console.error('Stripe or checkout info not available');
        setIsProcessing(false);
        clearTimeout(timeout);
      }
    } catch (error) {
      console.error('Stripe initialization failed:', error);
      setIsProcessing(false);
      clearTimeout(timeout);
    }
  };

  const handlePlaidSubmit = async () => {
    const { validateStatus } = plaidForm.validateForm();
    if (!validateStatus) return;

    setIsProcessing(true);
    try {
      await createPaymentAccount(plaidForm.formStore);

      await paymentAccountsActions.refetch();
      if (validatedPaymentAccountsOptions().length === 0) {
        await companySettingsActions.refetch();
      }

      setTimeout(() => {
        const currentPaymentMethods = validatedPaymentAccounts();
        if (currentPaymentMethods && currentPaymentMethods.length > 0) {
          props.onSuccess?.();
          handleClose();
          window.location.reload();
        } else {
          console.error('Payment method not detected after successful payment');
          setIsProcessing(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Plaid payment setup failed:', error);
      setIsProcessing(false);
    }
  };

  const handlePaymentMethodSelect = (method: 'stripe' | 'plaid') => {
    setPaymentMethod(method);
    if (method === 'stripe') {
      initStripeCheckout();
    }
  };

  const handleBack = () => {
    setPaymentMethod(null);
    cleanupStripe();
  };

  createEffect(() => {
    if (props.visible) {
      setPaymentMethod(null);
      cleanupStripe();
    } else {
      cleanupStripe();
    }
  });

  onCleanup(() => {
    cleanupStripe();
  });

  return (
    <Modal
      title={t('Add payment method')}
      visible={props.visible}
      onCancel={handleClose}
      showCloseButton={!paymentMethod()}
      showFooter={true}
      footer={
        <div class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:justify-between sm:p-6">
          <Show when={paymentMethod()}>
            <Button variant="outlined" onClick={handleBack} class="hidden w-full justify-center sm:block sm:w-auto">
              {t('Back')}
            </Button>
          </Show>
          <div class="order-1 flex w-full flex-col items-end justify-end gap-3 sm:flex-row">
            <Button variant="outlined" onClick={handleClose} class="w-full justify-center sm:w-auto">
              {t('Cancel')}
            </Button>
            <Show when={paymentMethod() === 'plaid'}>
              <Button onClick={handlePlaidSubmit} loading={isProcessing()} class="w-full justify-center sm:w-auto">
                {t('Add')}
              </Button>
            </Show>
          </div>
        </div>
      }
      class="flex max-h-[90vh] w-full max-w-4xl flex-col sm:mx-6 lg:mx-8">
      <div class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Show when={!paymentMethod()}>
          <div class="space-y-6">
            <div class="space-y-2 text-center">
              <h2 class="text-xl font-bold text-gray-900 sm:text-2xl">{t('Add payment method')}</h2>
              <p class="mx-auto max-w-2xl text-sm text-gray-600 sm:text-base">
                {t('Choose a payment method to continue using the application')}
              </p>
            </div>

            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <PaymentMethodButton
                onClick={() => handlePaymentMethodSelect('stripe')}
                disabled={isProcessing()}
                title={t('Credit/Debit cards')}
                subtitle={t('Add via {provider}', { provider: 'Stripe' })}
              />

              <PaymentMethodButton
                onClick={() => handlePaymentMethodSelect('plaid')}
                disabled={isProcessing()}
                title={t('Bank account')}
                subtitle={t('Add via {provider}', { provider: 'Plaid' })}
              />
            </div>

            <div class="mx-auto max-w-2xl">
              <div class="rounded-lg border p-4">
                <div class="flex items-start gap-3">
                  <IconLock class="size-5" />
                  <div class="text-sm">
                    <p class="mb-1 font-medium capitalize">{t('Secure payment processing')}</p>
                    <p>{t('Your payment information is encrypted and secure. We never store your full card details')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Show>

        <Show when={paymentMethod() === 'stripe'}>
          <div class="space-y-6">
            <SectionHeader
              title={t('Add credit/debit card')}
              subtitle={t('Complete your payment setup with {provider}', { provider: 'Stripe' })}
              onBack={handleBack}
            />

            <div class="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6">
              <Show
                when={isStripeReady()}
                fallback={
                  <div class="flex flex-col items-center justify-center py-12 sm:py-16">
                    <IconLoader class="mb-4 size-8 animate-spin text-blue-600" />
                    <p class="text-sm text-gray-600">{t('Loading secure payment form...')}</p>
                  </div>
                }>
                <div id="stripe-checkout-form" class="min-h-[400px]" />
              </Show>
            </div>
          </div>
        </Show>

        <Show when={paymentMethod() === 'plaid'}>
          <div class="space-y-6">
            <SectionHeader
              title={t('Add bank account')}
              subtitle={t('Connect your bank account securely with Plaid')}
              onBack={handleBack}
            />

            <div class="rounded-lg border border-gray-200 p-4 sm:p-6">
              <Form defaultForm={plaidForm} class="space-y-6">
                <LabeledBankingInput />
              </Form>
            </div>
          </div>
        </Show>
      </div>
    </Modal>
  );
};
